import React, { useEffect, useState } from "react";
import CostComparisonChart from "../components/common/CostComparisonChart";
import { fetchSmokingBenchmark } from "../services/benchmarkApi";
import "./CostCalculatorPage.css";

const formatAUD = (value) => {
  if (value >= 1000000) return `AUD ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `AUD ${(value / 1000).toFixed(1)}K`;
  return `AUD ${value.toFixed(2)}`;
};

const CostCalculatorPage = () => {
  const [dailyCigarettes, setDailyCigarettes] = useState("");
  const [pricePerPack, setPricePerPack] = useState("");
  const [benchmarkCigarettes, setBenchmarkCigarettes] = useState(0);
  const [benchmarkYear, setBenchmarkYear] = useState(null);
  const [benchmarkError, setBenchmarkError] = useState(false);

  useEffect(() => {
    const loadBenchmark = async () => {
      try {
        const data = await fetchSmokingBenchmark();
        const avg =
          data.average_daily_cigarettes !== undefined
            ? data.average_daily_cigarettes
            : data.value;
        setBenchmarkCigarettes(Number(avg) || 0);
        setBenchmarkYear(data.year !== undefined ? data.year : null);
        setBenchmarkError(false);
      } catch (error) {
        console.error("Failed to load smoking benchmark:", error);
        setBenchmarkCigarettes(0);
        setBenchmarkYear(null);
        setBenchmarkError(true);
      }
    };
    loadBenchmark();
  }, []);

  const safeDailyCigarettes = Math.max(0, Number(dailyCigarettes) || 0);
  const safePricePerPack = Math.max(0, Number(pricePerPack) || 0);
  const hasInput = dailyCigarettes !== "" && pricePerPack !== "";

  const dailyCost = (safeDailyCigarettes / 20) * safePricePerPack;
  const weeklyCost = dailyCost * 7;
  const monthlyCost = dailyCost * 30;
  const annualCost = dailyCost * 365;

  const absDailyCost = (benchmarkCigarettes / 20) * safePricePerPack;
  const absWeeklyCost = absDailyCost * 7;
  const absMonthlyCost = absDailyCost * 30;
  const absAnnualCost = absDailyCost * 365;

  const isZeroSmoking = hasInput && safeDailyCigarettes === 0;

  return (
    <main className="cost-calculator-page">

      {/* -- Header -- */}
      <header className="cost-calculator-page__header">
        <div className="cost-calculator-page__badge">💰 Cost Calculator</div>
        <h1 className="cost-calculator-page__heading">
          How much is smoking costing you?
        </h1>
        <p className="cost-calculator-page__subheading">
          Enter your daily cigarette count and pack price to see your real spending.
        </p>
      </header>

      {/* -- Two column layout -- */}
      <div className="cost-calculator-page__body">

        {/* -- Left: Input + amounts -- */}
        <div className="cost-calculator-page__left">

          {/* -- Input card -- */}
          <section className="cost-card">
            <h2 className="cost-card__title">Your smoking habits</h2>
            <div className="cost-card__inputs">
              <div className="cost-card__field">
                <label htmlFor="daily-cigarettes" className="cost-card__label">
                  🚬 Daily cigarette count
                </label>
                <input
                  id="daily-cigarettes"
                  type="number"
                  min="0"
                  max="200"
                  placeholder="e.g. 10"
                  value={dailyCigarettes}
                  onChange={(e) => setDailyCigarettes(e.target.value)}
                  className="cost-card__input"
                />
              </div>
              <div className="cost-card__field">
                <label htmlFor="price-per-pack" className="cost-card__label">
                  💵 Price per pack (AUD)
                </label>
                <input
                  id="price-per-pack"
                  type="number"
                  min="0"
                  max="200"
                  step="0.01"
                  placeholder="e.g. 35.00"
                  value={pricePerPack}
                  onChange={(e) => setPricePerPack(e.target.value)}
                  className="cost-card__input"
                />
              </div>
            </div>
          </section>

          {/* -- Amounts -- */}
          {hasInput ? (
            <>
              {/* -- Save banner -- */}
              {!isZeroSmoking && !benchmarkError && (
                <div className="cost-save-banner">
                  <span className="cost-save-banner__icon">💡</span>
                  <p className="cost-save-banner__text">
                    If you quit today, you could save{' '}
                    <strong>{formatAUD(annualCost)}</strong> this year alone.
                  </p>
                </div>
              )}

              <div className="cost-results">
                <div className="cost-results__card">
                  <div className="cost-results__accent cost-results__accent--weekly" />
                  <div className="cost-results__info">
                    <p className="cost-results__period">Weekly</p>
                  </div>
                  <p className="cost-results__amount">
                    {formatAUD(weeklyCost)}
                  </p>
                </div>

                <div className="cost-results__card">
                  <div className="cost-results__accent cost-results__accent--monthly" />
                  <div className="cost-results__info">
                    <p className="cost-results__period">Monthly</p>
                  </div>
                  <p className="cost-results__amount">
                    {formatAUD(monthlyCost)}
                  </p>
                </div>

                <div className="cost-results__card cost-results__card--highlight">
                  <div className="cost-results__accent cost-results__accent--annual" />
                  <div className="cost-results__info">
                    <p className="cost-results__period">Annual</p>
                    <p className="cost-results__sublabel">Total yearly cost</p>
                  </div>
                  <p className="cost-results__amount cost-results__amount--large">
                    {formatAUD(annualCost)}
                  </p>
                </div>
              </div>

              {isZeroSmoking && (
                <div className="cost-message cost-message--success">
                  Great job — you are saving money and protecting your health!
                </div>
              )}

              {benchmarkError && (
                <div className="cost-message cost-message--warning">
                  Benchmark comparison is temporarily unavailable.
                </div>
              )}

              {!benchmarkError && !isZeroSmoking && (
                <p className="cost-benchmark-note">
                  Compared against the ABS average of {benchmarkCigarettes.toFixed(1)} cigarettes
                  per day for Australian daily smokers
                  {benchmarkYear ? ` (${benchmarkYear})` : ""}.
                </p>
              )}
            </>
          ) : (
            <div className="cost-empty">
              <div className="cost-empty__icon">💸</div>
              <p className="cost-empty__text">
                Enter your details above to see your smoking costs.
              </p>
            </div>
          )}
        </div>

        {/* -- Right: Chart only -- */}
        <div className="cost-calculator-page__right">
          {hasInput && !isZeroSmoking && !benchmarkError ? (
            <section className="cost-chart-section">
              <div className="cost-chart-section__header">
                <h2 className="cost-chart-section__title">
                  Your cost vs Australian average
                </h2>
                <p className="cost-chart-section__subtitle">
                  Based on ABS data for daily smokers
                </p>
              </div>
              <CostComparisonChart
                myWeeklyCost={weeklyCost}
                myMonthlyCost={monthlyCost}
                myAnnualCost={annualCost}
                absWeeklyCost={absWeeklyCost}
                absMonthlyCost={absMonthlyCost}
                absAnnualCost={absAnnualCost}
              />
            </section>
          ) : (
            <div className="cost-chart-empty">
              <div className="cost-chart-empty__icon">📊</div>
              <p className="cost-empty__text">
                Your cost comparison chart will appear here.
              </p>
            </div>
          )}
        </div>

      </div>
    </main>
  );
};

export default CostCalculatorPage;